
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Company, Tenant, UserRole } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import Spinner from '../../components/common/Spinner';
import AddCompanyModal from '../../components/admin/AddCompanyModal';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { AuthContext } from '../../contexts/AuthContext';

const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>;
const DeleteIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>;

const CompaniesPage: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [tenants, setTenants] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const authContext = useContext(AuthContext);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
        const user = authContext?.user;
        if (!user) return;

        let fetchedCompanies: Company[] = [];
        
        if (user.role === UserRole.PLATFORM_OWNER) {
            fetchedCompanies = await firestoreService.getAllCompanies();
            // Cargar nombres de agencias para el mapa
            const allTenants = await firestoreService.getTenants();
            const tMap: Record<string, string> = {};
            allTenants.forEach(t => tMap[t.tenantId] = t.name);
            setTenants(tMap);
        } else if (user.tenantId) {
            fetchedCompanies = await firestoreService.getCompanies(user.tenantId);
        }
        
        setCompanies(fetchedCompanies);
    } catch (error) {
        console.error("Failed to fetch companies:", error);
    } finally {
        setLoading(false);
    }
  }, [authContext?.user]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleOpenModal = (company: Company | null) => {
      setEditingCompany(company);
      setIsModalOpen(true);
  }

  const handleCloseModal = () => {
      setEditingCompany(null);
      setIsModalOpen(false);
  }

  const handleSaveCompany = async (
      companyData: Omit<Company, 'companyId' | 'createdAt'> | Company
  ) => {
      try {
          if ('companyId' in companyData) {
              await firestoreService.updateCompany(companyData.companyId, companyData);
          } else {
              await firestoreService.addCompany(companyData);
          }
          handleCloseModal();
          fetchCompanies();
      } catch (error) {
          console.error("Failed to save company", error);
      }
  };

  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
  };

  const confirmDeleteCompany = async () => {
    if (!companyToDelete) return;
    setIsDeleting(true);
    try {
        await firestoreService.deleteCompany(companyToDelete.companyId);
        fetchCompanies();
        setCompanyToDelete(null);
    } catch (error) {
        console.error("Failed to delete company:", error);
    } finally {
        setIsDeleting(false);
    }
  };

  return (
    <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">Gestión de Clientes (Companies)</h1>
        <button 
          onClick={() => handleOpenModal(null)}
          className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors font-semibold shadow-lg shadow-blue-900/20"
        >
          Nuevo Cliente
        </button>
      </div>

      <div className="bg-gray-900 rounded-xl shadow-xl overflow-hidden border border-gray-800 min-h-[400px]">
        {loading ? (
          <div className="p-20 text-center"><Spinner size="lg" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
            {companies.length > 0 ? companies.map((company) => (
              <div key={company.companyId} className="bg-gray-800/40 rounded-xl p-6 flex flex-col items-center justify-center text-center relative group border border-gray-700/50 hover:border-blue-500/50 transition-all hover:bg-gray-800/60">
                 <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleOpenModal(company)} title="Editar" className="p-2 rounded-lg bg-gray-700 hover:bg-blue-600 transition-colors"><EditIcon/></button>
                    <button onClick={() => handleDeleteClick(company)} title="Eliminar" className="p-2 rounded-lg bg-gray-700 hover:bg-red-600 transition-colors"><DeleteIcon/></button>
                </div>
                
                {authContext?.user?.role === UserRole.PLATFORM_OWNER && (
                  <div className="absolute top-3 left-3">
                    <span className="text-[10px] uppercase font-bold px-2 py-1 bg-gray-900 text-gray-400 rounded-full border border-gray-700">
                      {tenants[company.tenantId] || 'Agencia'}
                    </span>
                  </div>
                )}

                <div className="h-20 flex items-center justify-center mb-4 mt-4">
                 {company.settings?.branding?.logoUrl ? (
                    <img src={company.settings.branding.logoUrl} alt={company.name} className="max-h-16 max-w-full object-contain" />
                 ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gray-700 flex items-center justify-center font-bold text-2xl shadow-inner" style={{backgroundColor: company.settings?.branding?.backgroundColor, color: company.settings?.branding?.primaryColor}}>
                      {company.name.charAt(0)}
                    </div>
                 )}
                </div>
                <h3 className="font-bold text-lg text-white mb-1">{company.name}</h3>
                <p className="text-xs text-gray-500 italic mb-4">{company.settings.branding.bannerText || 'Sin eslogan'}</p>
                
                <div className="mt-auto w-full h-1.5 rounded-full overflow-hidden bg-gray-700">
                   <div className="h-full" style={{backgroundColor: company.settings?.branding?.backgroundColor, width: '100%'}}></div>
                </div>
              </div>
            )) : (
              <div className="col-span-full py-20 text-center text-gray-500 italic">No hay clientes registrados aún.</div>
            )}
          </div>
        )}
      </div>

      {isModalOpen && (
        <AddCompanyModal 
          onClose={handleCloseModal}
          onSave={handleSaveCompany}
          companyToEdit={editingCompany}
        />
      )}

      <ConfirmationModal
        isOpen={!!companyToDelete}
        onClose={() => setCompanyToDelete(null)}
        onConfirm={confirmDeleteCompany}
        title="Eliminar Cliente"
        message={<>¿Estás seguro de eliminar a <strong>{companyToDelete?.name}</strong>? Se borrarán sus videos y análisis. Esta acción es definitiva.</>}
        isConfirming={isDeleting}
      />
    </div>
  );
};

export default CompaniesPage;
